import _get from 'lodash/get';
import _orderBy from 'lodash/orderBy';
import { CampaignType } from '../types/Campaign.js';
import { CheckoutEligibility } from '../types/product.js';
import { escapeUriPath } from './stringUtils.js';

/**
 * @returns rewards associated with a product & brand
 * - used in product bullet points widget
 * - return rewards only if it's > 0
 */
const getProductPageRewards = ({ productEntity, brandEntity, brandclub, retailers, }) => {
    const rewards = {};
    const sortOrder = [
        CampaignType.DailyDealsRewardCampaign,
        CampaignType.PurchaseRewardCampaign,
        CampaignType.MembershipRewardCampaign,
        CampaignType.SurveyRewardCampaign,
        CampaignType.AffiliateRewardCampaign,
        CampaignType.ContentRewardCampaign,
        CampaignType.ReferralRewardCampaign,
        CampaignType.RetailerConnectRewardCampaign,
    ];
    const rewardTypeToCampaignMapping = {
        join: CampaignType.MembershipRewardCampaign,
        survey: CampaignType.SurveyRewardCampaign,
        content: CampaignType.ContentRewardCampaign,
        customerReferral: CampaignType.ReferralRewardCampaign,
    };
    // product rewards
    if (Array.isArray(productEntity === null || productEntity === void 0 ? void 0 : productEntity.campaignRewards)) {
        for (const campaignReward of productEntity.campaignRewards) {
            if (typeof (campaignReward === null || campaignReward === void 0 ? void 0 : campaignReward.rewardAmount) === 'number' && typeof (campaignReward === null || campaignReward === void 0 ? void 0 : campaignReward.campaignType) === 'string') {
                rewards[campaignReward.campaignType] = Math.max((rewards === null || rewards === void 0 ? void 0 : rewards[campaignReward.campaignType]) || 0, campaignReward.rewardAmount);
            }
        }
    }
    // brand rewards
    if (typeof (brandEntity === null || brandEntity === void 0 ? void 0 : brandEntity.rewardAmounts) === 'object') {
        const rewardAmounts = Object.entries(brandEntity.rewardAmounts);
        for (const [key, value] of rewardAmounts) {
            if (Object.keys(rewardTypeToCampaignMapping).includes(key) && typeof (value === null || value === void 0 ? void 0 : value.rewardAmount) === 'number') {
                rewards[rewardTypeToCampaignMapping[key]] = value.rewardAmount;
            }
        }
    }
    // brandclub reward
    if (typeof (brandclub === null || brandclub === void 0 ? void 0 : brandclub.availableSurveyRewardAmount) === 'number') {
        rewards[CampaignType.SurveyRewardCampaign] = brandclub.availableSurveyRewardAmount;
    }
    // retailer connect reward
    const connectRewardAmount = (retailers || []).reduce((acc, retailer) => { var _a; return acc + ((_a = retailer === null || retailer === void 0 ? void 0 : retailer.retailerConnectRewardAmount) !== null && _a !== void 0 ? _a : 0); }, 0);
    if (connectRewardAmount) {
        rewards[CampaignType.RetailerConnectRewardCampaign] = connectRewardAmount;
    }
    return sortOrder
        .filter((rewardType) => !!(rewards === null || rewards === void 0 ? void 0 : rewards[rewardType]))
        .map((rewardType) => ({
        rewardType,
        rewardAmount: rewards[rewardType],
    }));
};
const buildRewardList = (rewards, brandclub, customerSpendByBrand) => {
    var _a;
    const mapList = [
        {
            id: CampaignType.PurchaseRewardCampaign,
            amount: 0,
            subTitle: 'for purchasing product',
        },
        {
            id: CampaignType.DailyDealsRewardCampaign,
            amount: 0,
            subTitle: 'for instant purchase',
        },
    ];
    if (customerSpendByBrand && brandclub && customerSpendByBrand.brandclubIds.indexOf(brandclub.brandclubId) >= 0) {
        mapList.push({
            id: CampaignType.SurveyRewardCampaign,
            amount: 0,
            subTitle: 'for completing surveys',
        });
    }
    mapList.push({
        id: CampaignType.AffiliateRewardCampaign,
        amount: 0,
        subTitle: 'affiliate commission',
    });
    mapList.push({
        id: CampaignType.ContentRewardCampaign,
        amount: 0,
        subTitle: 'in content rewards',
    });
    let res = [];
    const maxByType = {};
    rewards
        .filter(({ campaignStatus }) => campaignStatus === 'live')
        .forEach((rewardBase) => {
        // We want to blend purchase and limitedTimePurchaseRewardCampaign
        const reward = Object.assign({}, rewardBase);
        const rewardFound = mapList.find((item) => item.id === reward.campaignType);
        if (rewardFound) {
            if (maxByType[reward.campaignType]) {
                // if this reward amount is higher, replace
                if (reward.rewardAmount > maxByType[reward.campaignType].amount) {
                    maxByType[reward.campaignType] = Object.assign(Object.assign({}, rewardFound), { amount: reward.rewardAmount });
                }
            }
            else {
                maxByType[reward.campaignType] = Object.assign(Object.assign({}, rewardFound), { amount: reward.rewardAmount });
            }
        }
    });
    if (brandclub) {
        const surveyAmount = _get(brandclub, ['availableSurveyRewardAmount'], 0);
        const contentAmount = _get(brandclub, ['availableContentRewardAmount'], 0);
        const joinClubReward = (_a = brandclub === null || brandclub === void 0 ? void 0 : brandclub.campaignRewards) === null || _a === void 0 ? void 0 : _a.find((c) => c.campaignType === CampaignType.MembershipRewardCampaign);
        if (joinClubReward && !brandclub.isMember) {
            maxByType.promoteBrandClubRewardCampaign = {
                id: CampaignType.MembershipRewardCampaign,
                amount: joinClubReward.rewardAmount,
                subTitle: 'for joining the club',
            };
        }
        if (surveyAmount) {
            maxByType.surveyRewardCampaign = {
                id: CampaignType.SurveyRewardCampaign,
                amount: surveyAmount,
                subTitle: 'for completing surveys',
            };
        }
        if (contentAmount) {
            maxByType.contentRewardCampaign = {
                id: CampaignType.ContentRewardCampaign,
                amount: contentAmount,
                subTitle: 'in content rewards',
            };
        }
    }
    res = _orderBy(Object.values(maxByType), ['amount'], ['desc']);
    return res;
};
const getRewardTextFromCampaignType = (campaignType) => {
    switch (campaignType) {
        case CampaignType.DailyDealsRewardCampaign:
            return 'Daily Deals reward';
        case CampaignType.PurchaseRewardCampaign:
            return 'Purchase reward';
        case CampaignType.MembershipRewardCampaign:
            return 'Membership reward';
        case CampaignType.SurveyRewardCampaign:
            return 'Survey reward';
        case CampaignType.AffiliateRewardCampaign:
            return 'Affiliate reward';
        case CampaignType.ContentRewardCampaign:
            return 'Content reward';
        case CampaignType.ReferralRewardCampaign:
            return 'Referral reward';
        case CampaignType.RetailerConnectRewardCampaign:
            return 'Connect reward';
        default:
            return '';
    }
};
const getPurchaseRewardAmountForProduct = (product) => {
    var _a, _b;
    if (((_a = product.scores) === null || _a === void 0 ? void 0 : _a.maxPurchaseRewardPercent) && product.retailPrice) {
        return +((product.scores.maxPurchaseRewardPercent / 100) * product.retailPrice).toFixed(2);
    }
    // Base case: just purchase reward amount is set
    return ((_b = product.scores) === null || _b === void 0 ? void 0 : _b.maxPurchaseRewardAmount) || product.totalRewardAmount || 0;
};
const isProductEligibleForCheckout = (product, checkoutAllowedOfferType) => {
    const isOutOfStock = isProductOutOfStock(product, checkoutAllowedOfferType);
    return !isOutOfStock && isServerSideCheckoutEligible(product.checkoutEligibility);
};
const isProductOutOfStock = (product, checkoutAllowedOfferType) => {
    let isOutOfStock = !product.isAvailable;
    if (checkoutAllowedOfferType === 'firstPartyOnly' && !product.hasFirstPartyOffer) {
        // isOutOfStock = true; // we don't want to honor this flag for now
        console.warn(`1p is OOS: ${product.title}`);
    }
    else if (product.offers && product.offers.length > 0) {
        isOutOfStock = product.offers.every((offer) => offer.availabilityStatus === 'OutOfStock');
    }
    return isOutOfStock;
};
const isServerSideCheckoutEligible = (checkoutEligibility) => {
    return checkoutEligibility === CheckoutEligibility.SERVER_SIDE;
};
const getAddToBagText = (product, checkoutAllowedOfferType, disabledCheckout) => {
    return isProductEligibleForCheckout(product, checkoutAllowedOfferType) && !disabledCheckout ? '+ Add' : 'View';
};
const getProductUrl = (params) => {
    var _a;
    if (params.storeType === 'CustomDTCStore' && !params.dtcProductPageUrl) {
        return `#`;
    }
    if (params.dtcProductPageUrl) {
        return params.dtcProductPageUrl;
    }
    return `/${escapeUriPath((_a = params.title) === null || _a === void 0 ? void 0 : _a.substring(0, 40), '-')}/p/${params.stacklineSku}/product?${params.urlSearchParams.toString()}`;
};
const isInstantPurchaseRewardAvailable = (reward) => {
    const isInstantPurchase = reward.campaignType === CampaignType.DailyDealsRewardCampaign ||
        (reward.rewardUnlockStrategy === 'instant' && reward.campaignType === CampaignType.PurchaseRewardCampaign);
    const isLive = reward.campaignStatus === 'live';
    return isInstantPurchase && isLive;
};
const isPurchaseRewardAvailable = (reward) => {
    const isPurchase = reward.campaignType === CampaignType.PurchaseRewardCampaign;
    const isLive = reward.campaignStatus === 'live';
    return isPurchase && isLive;
};
const getPurchaseRewardAmountForProductV2 = (p) => {
    var _a, _b;
    let rewardType = 'purchase';
    let rewardAmount = 0;
    let rewardPercent = 0;
    const liveInstantPurchase = (_a = p === null || p === void 0 ? void 0 : p.campaignRewards) === null || _a === void 0 ? void 0 : _a.filter((reward) => {
        return isInstantPurchaseRewardAvailable(reward);
    });
    if (liveInstantPurchase && liveInstantPurchase.length > 0) {
        const maxInstantPurchaseReward = liveInstantPurchase.reduce((prev, current) => {
            if (current.rewardAmount > prev.rewardAmount) {
                return current;
            }
            return prev;
        });
        rewardType = 'instant';
        rewardAmount = maxInstantPurchaseReward.rewardAmount;
        rewardPercent = maxInstantPurchaseReward.rewardPercent || 0;
    }
    else {
        const livePurchase = (_b = p === null || p === void 0 ? void 0 : p.campaignRewards) === null || _b === void 0 ? void 0 : _b.filter((reward) => {
            return isPurchaseRewardAvailable(reward);
        });
        if (livePurchase && livePurchase.length > 0) {
            const maxPurchaseReward = livePurchase.reduce((prev, current) => {
                if (current.rewardAmount > prev.rewardAmount) {
                    return current;
                }
                return prev;
            });
            rewardAmount = maxPurchaseReward.rewardAmount;
            rewardPercent = maxPurchaseReward.rewardPercent || 0;
        }
    }
    return {
        type: rewardType,
        amount: rewardAmount,
        percent: rewardPercent,
    };
};
const getSortedBuyBoxProducts = (productsToList) => {
    if (!productsToList)
        return [];
    // Sort products by Add > Price > Rewards > push Out of Stock to bottom
    const sortedProducts = [...productsToList].sort((a, b) => {
        if (isProductOutOfStock(a) && !isProductOutOfStock(b))
            return 1;
        if (!isProductOutOfStock(a) && isProductOutOfStock(b))
            return -1;
        // check server only so that it is sync between web app and mobile app
        if (a.checkoutEligibility === CheckoutEligibility.SERVER_SIDE &&
            b.checkoutEligibility !== CheckoutEligibility.SERVER_SIDE)
            return -1;
        if (a.checkoutEligibility !== CheckoutEligibility.SERVER_SIDE &&
            b.checkoutEligibility === CheckoutEligibility.SERVER_SIDE)
            return 1;
        if (a.retailPrice && b.retailPrice) {
            const aReward = getPurchaseRewardAmountForProductV2(a);
            const bReward = getPurchaseRewardAmountForProductV2(b);
            if (a.retailPrice - aReward.amount < b.retailPrice - bReward.amount)
                return -1;
            if (a.retailPrice - aReward.amount > b.retailPrice - bReward.amount)
                return 1;
        }
        if (a.campaignRewards && b.campaignRewards) {
            const aReward = getPurchaseRewardAmountForProductV2(a);
            const bReward = getPurchaseRewardAmountForProductV2(b);
            if (aReward.amount > bReward.amount)
                return -1;
            if (aReward.amount < bReward.amount)
                return 1;
        }
        return 0;
    });
    return sortedProducts;
};

export { buildRewardList, getAddToBagText, getProductPageRewards, getProductUrl, getPurchaseRewardAmountForProduct, getPurchaseRewardAmountForProductV2, getRewardTextFromCampaignType, getSortedBuyBoxProducts, isProductEligibleForCheckout, isProductOutOfStock, isServerSideCheckoutEligible };
//# sourceMappingURL=productUtils.js.map
